import { ComponentStyleConfig } from '@chakra-ui/react';

import { textStyles } from '../../common';
import { getColorVar } from '@chakra-ui/theme-tools';

const outlineColor = 'state.selected';

const minHeightVar = '--tag-min-height',
  strokeVar = '--tag-stroke-color',
  bgVar = '--tag-bg';

const contrastColor = `oklch(from var(${bgVar}) clamp(0, (l / 0.623 - 1) * -infinity, 1) 0 h)`; // https://lea.verou.me/blog/2024/contrast-color/

export const tagStyles: Partial<ComponentStyleConfig> = {
  baseStyle: (_props) => ({
    container: {
      [strokeVar]: 'transparent',
      boxShadow: `inset 0 0 0 1px var(${strokeVar})`,
      borderRadius: 'base',
      px: 2,
      outline: '2px solid transparent',
      outlineOffset: 0,
      display: 'inline-flex',
      color: contrastColor,
      _focusVisible: {
        outlineColor,
      },
      ':has(:focus-visible)': {
        outlineColor,
      },
      'svg:not(button > *)': {
        lineHeight: 'revert-layer',
        width: '1lh',
        height: '1lh',
      },
    },
    closeButton: {
      height: `var(${minHeightVar})`,
      width: `var(${minHeightVar})`,
      my: -2,
      mr: -2,
      bgColor: 'transparent',
      color: 'inherit',
      opacity: 1,
      '&&': {
        boxShadow: 'none',
      },
      _hover: {
        bgColor: 'whiteAlpha.300',
      },
      _pressed: {
        bgColor: 'whiteAlpha.400',
      },
      _light: {
        _focusVisible: {
          bgColor: 'blackAlpha.200',
        },
        _hover: {
          bgColor: 'blackAlpha.200',
        },
        _pressed: {
          bgColor: 'blackAlpha.300',
        },
      },
      _disabled: {
        bgColor: 'transparent',
        opacity: 0.5,
      },
    },
  }),
  sizes: {
    xs: {
      container: {
        [minHeightVar]: 'var(--chakra-sizes-4)',
        ...textStyles['body-xs'],
        py: 'px',
      },
      closeButton: {
        fontSize: 'sm',
      },
    },
    sm: {
      container: {
        ...textStyles['body-sm'],
        py: 0.5,
      },
      label: {
        fontWeight: '500',
      },
      closeButton: {
        fontSize: 'md',
      },
    },
    md: {
      container: {
        ...textStyles['body-sm'],
        py: 1,
      },
      label: {
        fontWeight: '500',
      },
      closeButton: {
        fontSize: 'md',
      },
    },
    lg: {
      container: {
        ...textStyles['body-md'],
        py: 1.5,
      },
      label: {
        fontWeight: '500',
      },
      closeButton: {
        fontSize: 'lg',
      },
    },
  },
  variants: {
    solid: (props) => ({
      container: {
        [bgVar]: getColorVar(
          props.theme,
          `${props.colorScheme}.${props.colorScheme === 'gray' ? 5 : 6}00`,
        ),
        [strokeVar]: getColorVar(props.theme, 'whiteAlpha.300'),
        _light: {
          [bgVar]: getColorVar(props.theme, `${props.colorScheme}.100`),
          [strokeVar]: getColorVar(props.theme, 'blackAlpha.300'),
        },
      },
    }),
    subtle: (props) => ({
      container: {
        [bgVar]: getColorVar(
          props.theme,
          `${props.colorScheme}.${{ gray: 6, corelight: 9, pink: 9, red: 9, yellow: 9 }[props.colorScheme] || 8}00`,
        ),
        color: `${props.colorScheme}.${{ corelight: 4, red: 3, orange: 3 }[props.colorScheme] || 2}00`,
        _light: {
          [bgVar]: getColorVar(props.theme, `${props.colorScheme}.50`),
          color: getColorVar(
            props.theme,
            `${props.colorScheme}.${{ gray: 6, corelight: 9, pink: 9, red: 9, yellow: 9 }[props.colorScheme] || 8}00`,
          ),
          [strokeVar]: getColorVar(props.theme, 'blackAlpha.50'),
        },
      },
    }),
    outline: (props) => ({
      container: {
        color: `${props.colorScheme}.200`,
        [strokeVar]: getColorVar(props.theme, `${props.colorScheme}.400`),
        _light: {
          color: `${props.colorScheme}.600`,
          [strokeVar]: getColorVar(props.theme, `${props.colorScheme}.200`),
        },
      },
    }),
  },
};
